import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Check, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminBgChecks() {
  const [checks, setChecks] = useState([]);
  const load = useCallback(() => api.get("/admin/background-checks").then((r) => setChecks(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const review = async (id, status) => {
    await api.put(`/admin/background-checks/${id}`, { status });
    toast.success(`Background check ${status}`);
    load();
  };

  const pending = checks.filter((c) => c.status === "pending");
  const reviewed = checks.filter((c) => c.status !== "pending");

  return (
    <div className="space-y-6" data-testid="admin-bgchecks">
      <div>
        <h1 className="font-heading font-black text-3xl tracking-tight text-sapphire">Background Check Queue</h1>
        <p className="text-muted-foreground text-sm mt-1">{pending.length} pending review.</p>
      </div>

      {checks.length === 0 && (
        <div className="border border-dashed border-border rounded-md p-12 text-center text-muted-foreground">
          <ShieldCheck className="w-8 h-8 mx-auto mb-3 opacity-40" /> No submissions yet.
        </div>
      )}

      {pending.map((c) => (
        <div key={c.id} className="border border-signal/40 bg-signal/5 rounded-md p-5 flex items-center justify-between" data-testid={`bgcheck-${c.id}`}>
          <div>
            <div className="font-semibold text-sapphire">{c.full_name}</div>
            <div className="text-sm text-muted-foreground">{c.cleaner?.email} · DOB {c.dob} · SSN ••••{c.ssn_last4}</div>
            <div className="text-xs text-muted-foreground mt-1">Submitted {new Date(c.created_at).toLocaleString()}</div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-600 hover:text-white" onClick={() => review(c.id, "denied")} data-testid={`deny-${c.id}`}><X className="w-4 h-4 mr-1" /> Deny</Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => review(c.id, "approved")} data-testid={`approve-${c.id}`}><Check className="w-4 h-4 mr-1" /> Approve</Button>
          </div>
        </div>
      ))}

      {reviewed.length > 0 && (
        <div>
          <h2 className="font-heading font-bold text-lg text-sapphire mb-2">Reviewed</h2>
          <div className="border border-border rounded-md bg-white divide-y divide-border">
            {reviewed.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-5 py-3">
                <div><span className="font-semibold text-sapphire">{c.full_name}</span> <span className="text-sm text-muted-foreground">· {c.cleaner?.email}</span></div>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded ${c.status === "approved" ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>{c.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
